"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

import { useCookieConsent } from "@/lib/legal/cookie-consent";

import {
  identifyClarityVisitor,
  readClarityProjectId,
  startClarity,
  tagClaritySession,
  upgradeClaritySession,
} from "./clarity";
import { loadClarityIdentity } from "./clarity-identity";

/**
 * Starts Microsoft Clarity — **only** after the visitor accepted, and only when a project is
 * configured.
 *
 * ```
 * consent === "aceito" && NEXT_PUBLIC_CLARITY_PROJECT_ID set  → starts
 * anything else (pending, refused, unconfigured)              → nothing
 * ```
 *
 * Pending is not consent. Clarity must not load before the accept under any circumstance; that is
 * the whole point of the notice. Unset in dev, test and preview, so those environments stay out
 * of production data (same reasoning as `GtmContainer`).
 *
 * There are two doors, and `startClarity` is idempotent so both can be open:
 *
 * - `instrumentation-client.ts` — someone who already accepted on a past visit. It runs before
 *   hydration, so short visits and the start of every page are recorded;
 * - this effect — someone who accepts right now, in the banner. The consent changes in place,
 *   with no navigation, so only a component that listens to it can react.
 *
 * Starting sends the consent signal (`consentV2`) with the tag, so a Clarity project in Consent
 * Mode does not split each page view into a session of its own (see `clarity.ts`).
 *
 * Identification: on every page — the App Router's client navigations count as pages for Clarity,
 * and it asks for `identify` on each — the visitor is tied to the session (`identify`, and
 * `upgrade` so an identified session survives the daily sampling) and tagged (`email`,
 * `viewer_role`, ids). The identity is asked of `/api/analytics/clarity-identity` once per
 * document (`clarity-identity.ts`), and only after the accept: with no consent there is no
 * request at all. The email in plain text is a deliberate exception to the project's "no PII in
 * analytics" rule, valid for Clarity only.
 *
 * Considered and not chosen: firing Clarity from a GTM tag on a `dataLayer` event. It fits the
 * "GTM is loading only" rule, but moves the delivery into configuration on the GTM panel, which
 * is outside this repository's control and is what the deadline does not tolerate.
 *
 * Accepting is not undone by refusing later in the same page: a script that already ran cannot be
 * unloaded. There is no screen to change the answer yet.
 */
export function ClarityContainer() {
  const { consent } = useCookieConsent();
  const pathname = usePathname();

  useEffect(() => {
    if (consent !== "aceito") return;

    const projectId = readClarityProjectId();
    if (!projectId) return;

    startClarity(projectId);

    // The pathname is a dependency on purpose: Clarity wants the identification on each page.
    let current = true;
    void loadClarityIdentity().then((identity) => {
      if (!current) return;

      if (identity.customId !== null && identity.friendlyName !== null) {
        identifyClarityVisitor(identity.customId, identity.friendlyName);
        upgradeClaritySession("identified_visitor");
      }
      tagClaritySession(identity.tags);
    });

    return () => {
      current = false;
    };
  }, [consent, pathname]);

  return null;
}
